import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import API_BASE_URL from '../config/api';
import { getFilteredDishes, getFilteredRestaurants, getFilterOptions } from '../services/filtersApi';
import { Filter, X } from 'lucide-react';

// Import des composants modulaires
import HeroSection from '../components/home/sections/HeroSection';
import CategoriesSection from '../components/home/sections/CategoriesSection';
import DishesSection from '../components/home/sections/DishesSection';
import RestaurantsSection from '../components/home/sections/RestaurantsSection';
import PromotionsSectionUpdated from '../components/home/sections/PromotionsSectionUpdated';
import PromotionManager from '../components/promotion/PromotionManager';
import PromotionsFloatingManager from '../components/promotion/PromotionsFloatingManager';
import PromotionsBannerManager from '../components/promotion/PromotionsBannerManager';
import FiltersPanel from '../components/home/filters/FiltersPanel';
import RestaurantFiltersPanel from '../components/home/filters/RestaurantFiltersPanel';
import { getNearbyRestaurants, getClientProfile } from '../services/api';
import { getCateringServices } from '../services/cateringApi';
import useGroceryFeed from '../hooks/useGroceryFeed';
import GroceryShopSection from '../components/home/sections/GroceryShopSection';
import GroceryShopsCarousel from '../components/home/sections/GroceryShopsCarousel';
import CateringServicesCarousel from '../components/home/sections/CateringServicesCarousel';
import PopularDishesCarousel from '../components/home/sections/PopularDishesCarousel';
import SectionHeader from '../components/home/sections/SectionHeader';
import { getGroceryCategoryIcon } from '../utils/groceryCategoryIcon';

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();

  // Recherche depuis l'URL (?q=...) — soumise depuis la navbar
  const [searchQueryFromUrl, setSearchQueryFromUrl] = useState(null);
  const [searchResultsDishes, setSearchResultsDishes] = useState([]);
  const [searchResultsRestaurants, setSearchResultsRestaurants] = useState([]);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);

  // États principaux
  const [dishes, setDishes] = useState([]);
  const [filteredDishes, setFilteredDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [dishesLoading, setDishesLoading] = useState(true);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const {
    groceryShops,
    selectedGroceryShopId,
    setSelectedGroceryShopId,
    selectedGroceryCategory,
    groceryShopProducts,
    groceryShopLoading,
    groceryCategories,
    filteredGroceryProducts,
    pagedGroceryProducts,
    setGroceryPage,
    groceryPageSafe,
    groceryTotalPages,
    handleGroceryPageChange,
    handleGroceryCategoryClick,
  } = useGroceryFeed();
  const [cateringServices, setCateringServices] = useState([]);
  const [cateringLoading, setCateringLoading] = useState(true);
  const [cateringError, setCateringError] = useState(null);
  const [popularDishes, setPopularDishes] = useState([]);
  const [popularDishesLoading, setPopularDishesLoading] = useState(true);

  // États pour les onglets et filtres
  const [tab, setTab] = useState('dishes');
  const tabsSectionRef = useRef(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const itemsPerPage = 16;

  // États pour les filtres
  const defaultDishFilters = {
    priceRange: { min: 0, max: 100 },
    delivery: [],
    rating: [],
    diet: [],
    cuisine: []
  };
  const defaultRestaurantFilters = {
    cuisine: [],
    priceRange: { min: 0, max: 200 },
    rating: [],
    city: [],
    status: []
  };

  const [draftDishFilters, setDraftDishFilters] = useState(defaultDishFilters);
  const [appliedDishFilters, setAppliedDishFilters] = useState(defaultDishFilters);

  const [draftRestaurantFilters, setDraftRestaurantFilters] = useState(defaultRestaurantFilters);
  const [appliedRestaurantFilters, setAppliedRestaurantFilters] = useState(defaultRestaurantFilters);

  const [expandedSections, setExpandedSections] = useState({
    price: true,
    delivery: false,
    rating: false,
    diet: false,
    cuisine: false
  });

  const [expandedRestaurantSections, setExpandedRestaurantSections] = useState({
    price: true,
    rating: false,
    cuisine: false,
    services: false
  });

  // Mode de service restaurant : 'all' | 'delivery' | 'pickup'
  const [serviceMode, setServiceMode] = useState('all');

  // États pour le tri
  const [sortBy, setSortBy] = useState('popularity');
  const [restaurantSortBy, setRestaurantSortBy] = useState('popularity');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showRestaurantSortDropdown, setShowRestaurantSortDropdown] = useState(false);

  // États pour les popups
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showRestaurantPopup, setShowRestaurantPopup] = useState(false);
  const [showDishPopup, setShowDishPopup] = useState(false);
  const [dishPopupData, setDishPopupData] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(true); // Pour desktop aussi

  // Chargement des options de filtres disponibles
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const options = await getFilterOptions();
        console.log('Options de filtres disponibles:', options);
        // Les options peuvent être utilisées pour peupler dynamiquement les filtres
      } catch (err) {
        console.error("Erreur lors du chargement des options de filtres :", err);
      }
    };

    fetchFilterOptions();
  }, []);

  // Chargement des plats les plus aimés (triés par note côté backend)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPopularDishesLoading(true);
      try {
        const data = await getFilteredDishes({ skip: 0, limit: 12, sortBy: 'rating', sortOrder: 'desc' });
        if (!cancelled) setPopularDishes(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPopularDishes([]);
      } finally {
        if (!cancelled) setPopularDishesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Onglet initial depuis l'URL (?tab=groceryShop, lien sidebar "Grocery shop")
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'groceryShop' || tabParam === 'dishes' || tabParam === 'restaurants') {
      setTab(tabParam);
      setTimeout(() => {
        tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recherche depuis l'URL (?q=...) — exécutée quand on soumet depuis la navbar
  useEffect(() => {
    const q = searchParams.get('q');
    if (!q || !q.trim()) {
      setSearchQueryFromUrl(null);
      setSearchResultsDishes([]);
      setSearchResultsRestaurants([]);
      return;
    }
    const query = q.trim();
    const cityParam = searchParams.get('city');
    const citySuffix = cityParam ? `&city=${encodeURIComponent(cityParam)}` : '';
    setSearchQueryFromUrl(query);
    setSearchResultsLoading(true);
    setCurrentPage(1);
    (async () => {
      try {
        const [dishesRes, restaurantsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/dishes/search?q=${encodeURIComponent(query)}${citySuffix}`).then(r => r.json()),
          fetch(`${API_BASE_URL}/restaurants/search?q=${encodeURIComponent(query)}${citySuffix}`).then(r => r.json())
        ]);
        setSearchResultsDishes(Array.isArray(dishesRes) ? dishesRes : []);
        setSearchResultsRestaurants(Array.isArray(restaurantsRes) ? restaurantsRes : []);
      } catch (err) {
        console.error('Erreur recherche URL:', err);
        setSearchResultsDishes([]);
        setSearchResultsRestaurants([]);
      } finally {
        setSearchResultsLoading(false);
      }
    })();
  }, [searchParams]);

  // Chargement des données
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const res = await fetch(`${API_BASE_URL}/categories/`);
        const data = await res.json();
        setCategories(data.slice(0, 12));
      } catch (err) {
        console.error("Erreur lors du chargement des catégories :", err);
        const fallbackCategories = [
          { id: 1, name: t('home.fallbackCategories.entree') },
          { id: 2, name: t('home.fallbackCategories.dish') },
          { id: 3, name: t('home.fallbackCategories.dessert') },
          { id: 4, name: t('home.fallbackCategories.drink') },
          { id: 5, name: t('home.fallbackCategories.snack') },
          { id: 6, name: t('home.fallbackCategories.vegetarian') },
          { id: 7, name: t('home.fallbackCategories.breakfast') },
          { id: 8, name: t('home.fallbackCategories.soup') },
          { id: 9, name: t('home.fallbackCategories.grill') }
        ];
        setCategories(fallbackCategories);
      } finally {
        setCategoriesLoading(false);
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Charger TOUS les plats avec filtres (sans limite pour la pagination côté client)
  useEffect(() => {
    const fetchAllDishes = async () => {
      try {
        setDishesLoading(true);
        // Charger tous les plats (limite élevée ou sans limite)
        // On charge d'abord un grand nombre pour avoir tous les plats disponibles
        const data = await getFilteredDishes({
          filters: appliedDishFilters,
          skip: 0,
          limit: 1000, // Limite élevée pour charger tous les plats
          sortBy: sortBy === 'popularity' ? 'created_at' : sortBy.includes('price') ? 'price' : 'name',
          sortOrder: sortBy === 'price-high' || sortBy === 'price_desc' ? 'desc' : 'asc',
          categoryId: selectedCategoryId
        });
        console.log('📦 Plats chargés:', data?.length || 0);
        setDishes(data || []);
        setFilteredDishes(data || []);
        // Réinitialiser à la page 1 quand les filtres changent
        setCurrentPage(1);
      } catch (err) {
        console.error("Erreur lors du chargement des plats :", err);
        // Fallback en cas d'erreur - charger sans limite
        try {
          const res = await fetch(`${API_BASE_URL}/dishes?skip=0&limit=1000`);
          const data = await res.json();
          console.log('📦 Plats chargés (fallback):', data?.length || 0);
          setDishes(data || []);
          setFilteredDishes(data || []);
          setCurrentPage(1);
        } catch (fallbackErr) {
          console.error("Erreur fallback:", fallbackErr);
          setDishes([]);
          setFilteredDishes([]);
        }
      } finally {
        setDishesLoading(false);
      }
    };

    fetchAllDishes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Retirer currentPage des dépendances pour ne pas recharger à chaque changement de page
    sortBy, 
    selectedCategoryId, 
    JSON.stringify(appliedDishFilters) // Sérialiser pour éviter les boucles infinies
  ]);

  // Charger les restaurants avec filtres
  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setRestaurantsLoading(true);
        const data = await getFilteredRestaurants({
          filters: appliedRestaurantFilters,
          skip: 0,
          limit: 100,
          sortBy: restaurantSortBy === 'popularity' ? 'rating' : restaurantSortBy,
          sortOrder: 'desc'
        });
        // Gérer le cas où l'API retourne un objet avec 'restaurants' ou directement un tableau
        const restaurantsList = Array.isArray(data) ? data : (data.restaurants || []);
        setRestaurants(restaurantsList);
      } catch (err) {
        console.error("Erreur lors du chargement des restaurants :", err);
        // Fallback en cas d'erreur
        try {
          const res = await fetch(`${API_BASE_URL}/restaurants/`);
          const data = await res.json();
          // Gérer le cas où l'API retourne un objet avec 'restaurants' ou directement un tableau
          const restaurantsList = Array.isArray(data) ? data : (data.restaurants || []);
          setRestaurants(restaurantsList);
        } catch (fallbackErr) {
          console.error("Erreur fallback:", fallbackErr);
        }
      } finally {
        setRestaurantsLoading(false);
      }
    };

    fetchRestaurants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    restaurantSortBy, 
    JSON.stringify(appliedRestaurantFilters) // Sérialiser pour éviter les boucles infinies
  ]);

  useEffect(() => {
    const getBrowserPosition = () => new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
        () => resolve(null),
        { timeout: 5000, maximumAge: 5 * 60 * 1000 }
      );
    });

    const fetchNearbyRestaurants = async () => {
      try {
        const coords = await getBrowserPosition();

        let city = '';
        if (!coords && localStorage.getItem('access_token')) {
          try {
            const profile = await getClientProfile();
            const rawAddress = profile?.address || '';
            if (rawAddress) {
              const parts = rawAddress.split(',').map(p => p.trim()).filter(Boolean);
              city = parts.length > 0 ? parts[parts.length - 1] : '';
            }
          } catch (_) {
            city = '';
          }
        }

        const data = await getNearbyRestaurants({
          lat: coords?.lat,
          lng: coords?.lng,
          city: city || undefined,
          limit: 6,
        });
        const restaurantsList = Array.isArray(data) ? data : (data.restaurants || []);
        setNearbyRestaurants(restaurantsList);
      } catch (err) {
        setNearbyRestaurants([]);
      }
    };

    fetchNearbyRestaurants();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadServices = async () => {
      try {
        setCateringLoading(true);
        setCateringError(null);
        const data = await getCateringServices({ is_active: true, limit: 12 });
        if (!cancelled) {
          setCateringServices(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setCateringError(t('home.cateringLoadError'));
          setCateringServices([]);
        }
      } finally {
        if (!cancelled) {
          setCateringLoading(false);
        }
      }
    };

    loadServices();

    return () => { cancelled = true; };
  }, []);

  // Fonctions de gestion
  const fetchDishesByCategory = async (categoryId) => {
    try {
      setDishesLoading(true);
      const res = await fetch(`${API_BASE_URL}/dishes/by_category/${categoryId}`);
      const data = await res.json();
      setFilteredDishes(Array.isArray(data) ? data : (data?.dishes ?? data?.results ?? []));
    } catch (error) {
      console.error("Erreur lors du chargement des plats par catégorie :", error);
    } finally {
      setDishesLoading(false);
    }
  };

  const handleSearchResults = (results) => {
    if (!results || results.length === 0) {
      setFilteredDishes(dishes || []);
    } else {
      setFilteredDishes(results);
    }
    setCurrentPage(1);
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategoryId(categoryId);
    fetchDishesByCategory(categoryId);
  };

  const handleDishCardClick = (dish) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/product/${dish.id}`);
  };

  const handleDishPlusClick = (dish) => {
    setDishPopupData(dish);
    setQuantity(1);
    setShowDishPopup(true);
  };

  const handleRestaurantCardClick = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setShowRestaurantPopup(true);
  };

  // Fonctions pour les filtres
  const toggleFilter = (filterType, value) => {
    setDraftDishFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const clearAllFilters = () => {
    setDraftDishFilters(defaultDishFilters);
  };

  const handlePriceRangeChange = (range) => {
    setDraftDishFilters(prev => ({
      ...prev,
      priceRange: range
    }));
  };

  const handleRestaurantPriceRangeChange = (range) => {
    setDraftRestaurantFilters(prev => ({
      ...prev,
      priceRange: range
    }));
  };

  const toggleRestaurantFilter = (filterType, value) => {
    setDraftRestaurantFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] && prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...(prev[filterType] || []), value]
    }));
  };

  const toggleRestaurantSection = (section) => {
    setExpandedRestaurantSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const clearAllRestaurantFilters = () => {
    setDraftRestaurantFilters(defaultRestaurantFilters);
  };

  const applyDishFilters = () => {
    setAppliedDishFilters(draftDishFilters);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  const applyRestaurantFilters = () => {
    setAppliedRestaurantFilters(draftRestaurantFilters);
    setShowMobileFilters(false);
  };

  // Raccourcis de filtre rapide (pills), appliqués immédiatement
  const isHighestRatedActive = tab === 'restaurants'
    ? draftRestaurantFilters.rating?.includes('4.5')
    : draftDishFilters.rating?.includes('4.5');
  const isFreeDeliveryActive = draftDishFilters.delivery?.includes('free');

  const toggleQuickHighestRated = () => {
    if (tab === 'restaurants') {
      const next = { ...draftRestaurantFilters, rating: isHighestRatedActive ? [] : ['4.5'] };
      setDraftRestaurantFilters(next);
      setAppliedRestaurantFilters(next);
    } else {
      const next = { ...draftDishFilters, rating: isHighestRatedActive ? [] : ['4.5'] };
      setDraftDishFilters(next);
      setAppliedDishFilters(next);
      setCurrentPage(1);
    }
  };

  const toggleQuickFreeDelivery = () => {
    const next = { ...draftDishFilters, delivery: isFreeDeliveryActive ? [] : ['free'] };
    setDraftDishFilters(next);
    setAppliedDishFilters(next);
    setCurrentPage(1);
  };

  // Données affichées : résultats de recherche (URL ?q=) ou contenu normal
  const effectiveFilteredDishes = (() => {
    const raw = searchQueryFromUrl ? searchResultsDishes : filteredDishes;
    return Array.isArray(raw) ? raw : [];
  })();
  const effectiveRestaurants = searchQueryFromUrl ? searchResultsRestaurants : (restaurants || []);

  // IDs des restaurants offrant le retrait sur place
  const pickupRestaurantIds = useMemo(
    () => new Set((restaurants || []).filter((r) => r.accept_pickup === true).map((r) => String(r.id))),
    [restaurants],
  );

  // Appliquer le filtre service sur les plats (pickup = seulement les plats des restos avec retrait)
  const serviceDishes = useMemo(() => {
    if (serviceMode !== 'pickup') return effectiveFilteredDishes;
    return effectiveFilteredDishes.filter((d) =>
      pickupRestaurantIds.has(String(d.restaurant_id ?? d.restaurant?.id ?? ''))
    );
  }, [effectiveFilteredDishes, serviceMode, pickupRestaurantIds]);

  // Pagination côté client
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDishes = serviceDishes.length > 0 ? serviceDishes.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = serviceDishes.length > 0 ? Math.ceil(serviceDishes.length / itemsPerPage) : 0;
  
  const clearSearchFromUrl = () => {
    setSearchParams({});
    setSearchQueryFromUrl(null);
    setSearchResultsDishes([]);
    setSearchResultsRestaurants([]);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Appliquer le filtre catégorie + filtre service (pickup/delivery) sur les restaurants
  const filteredRestaurants = (() => {
    if (searchQueryFromUrl) {
      const list = searchResultsRestaurants || [];
      if (serviceMode === 'pickup') return list.filter((r) => r.accept_pickup === true);
      return list;
    }
    let base = restaurants || [];
    if (selectedCategoryId && filteredDishes && filteredDishes.length > 0) {
      const restaurantIdsInCategory = new Set(
        filteredDishes
          .map((d) => String(d.restaurant_id ?? d.restaurant?.id ?? ''))
          .filter(Boolean)
      );
      const narrowed = base.filter((r) => restaurantIdsInCategory.has(String(r.id)));
      if (narrowed.length === 0 && base.length > 0) {
        // garder base non filtrée par catégorie si intersection vide
      } else {
        base = narrowed;
      }
    }
    if (serviceMode === 'pickup') return base.filter((r) => r.accept_pickup === true);
    // 'delivery' ou 'all' → pas de filtre supplémentaire (tous livrent par défaut)
    return base;
  })();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar onSearchResults={handleSearchResults} />
        <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 animate-pulse">
          {/* Hero placeholder */}
          <div className="h-48 bg-gray-200 rounded-2xl mt-6 mb-6" />
          {/* Category pills */}
          <div className="flex gap-3 mb-8 overflow-hidden">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-9 w-24 bg-gray-200 rounded-full flex-shrink-0" />
            ))}
          </div>
          {/* Dish card grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden border border-gray-100">
                <div className="h-40 bg-gray-200" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-4 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <NavBar onSearchResults={handleSearchResults} />
      <div className='w-full max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8'>
        
        {/* Hero Section with Categories */}
        <HeroSection 
          onSearchResults={handleSearchResults}
          categories={categories}
          categoriesLoading={categoriesLoading}
          selectedCategoryId={selectedCategoryId}
          onCategoryClick={handleCategoryClick}
        />
        
        {/* Plats les plus aimés */}
        {!searchQueryFromUrl && (
          <PopularDishesCarousel
            dishes={popularDishes}
            loading={popularDishesLoading}
            onDishCardClick={handleDishCardClick}
            onDishPlusClick={handleDishPlusClick}
          />
        )}

        {/* Résultats de recherche depuis la navbar (?q=...) */}
        {searchQueryFromUrl && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg bg-primary-50 border border-namke-darkGreen/15 px-4 py-3">
            <span className="text-namke-black/80">
              {t('home.searchResultsFor', { query: searchQueryFromUrl })}
              {searchResultsLoading ? t('home.loadingSuffix') : ''}
            </span>
            <button
              type="button"
              onClick={clearSearchFromUrl}
              className="text-sm font-medium text-[rgb(5, 70, 37)] hover:text-[rgb(3, 40, 22)] underline"
            >
              {t('home.clearSearch')}
            </button>
          </div>
        )}

        {/* Featured Items Section with Tabs */}
        {!searchQueryFromUrl && nearbyRestaurants.length > 0 && (
          <div className="mb-8">
            <SectionHeader title={t('home.nearbyRestaurants')} onSeeAllClick={() => setTab('restaurants')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {nearbyRestaurants.map((restaurant) => (
                <div
                  key={`nearby-${restaurant.id}`}
                  className="bg-namke-white border border-namke-darkGreen/12 rounded-2xl p-4 transition-shadow cursor-pointer hover:shadow-md"
                  onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                >
                  <p className="font-display text-base font-semibold text-namke-black truncate">{restaurant.name}</p>
                  <p className="text-sm text-namke-black/55 mt-1 truncate">
                    {restaurant.city || restaurant.address || t('home.locationUnknown')}
                  </p>
                  <p className="text-sm mt-2 text-[rgb(5, 70, 37)]">
                    {restaurant.rating != null ? `${Number(restaurant.rating).toFixed(1)} ★` : t('home.newBadge')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div ref={tabsSectionRef}>
          {/* Filtres rapides */}
          {tab !== 'groceryShop' && (
            <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide">
              <button
                type="button"
                onClick={() => setShowMobileFilters(true)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium bg-namke-cream text-namke-black/80 hover:bg-primary-100 transition-colors"
              >
                <Filter size={14} />
                {t('home.filters')}
              </button>
              {tab === 'dishes' && (
                <button
                  type="button"
                  onClick={toggleQuickFreeDelivery}
                  className={`flex-shrink-0 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                    isFreeDeliveryActive ? 'bg-[rgb(5,70,37)] text-white' : 'bg-namke-cream text-namke-black/80 hover:bg-primary-100'
                  }`}
                >
                  {t('home.freeDelivery')}
                </button>
              )}
              <button
                type="button"
                onClick={toggleQuickHighestRated}
                className={`flex-shrink-0 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  isHighestRatedActive ? 'bg-[rgb(5,70,37)] text-white' : 'bg-namke-cream text-namke-black/80 hover:bg-primary-100'
                }`}
              >
                {t('home.topRated')}
              </button>
              <button
                type="button"
                onClick={() => (tab === 'restaurants' ? setShowRestaurantSortDropdown(true) : setShowSortDropdown(true))}
                className="flex-shrink-0 px-3.5 py-2 rounded-full text-sm font-medium bg-namke-cream text-namke-black/80 hover:bg-primary-100 transition-colors"
              >
                {t('home.sort')}
              </button>
            </div>
          )}

          {tab === 'restaurants' && (
            <div className="flex flex-col lg:flex-row gap-6 mb-8">
              {/* Bouton pour afficher/masquer les filtres - invisible sur grands écrans */}
              <div className="flex items-center justify-between mb-4 lg:hidden">
                <button
                  onClick={() => {
                    setShowMobileFilters(!showMobileFilters);
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-namke-darkGreen text-white rounded-full hover:bg-namke-black transition-colors"
                >
                  <Filter className="w-5 h-5" />
                  <span>{showMobileFilters ? t('home.hideFilters') : t('home.showFilters')}</span>
                </button>
              </div>

              {/* Sidebar des filtres pour restaurants */}
              <div className={`w-full lg:w-80 flex-shrink-0 transition-all duration-300 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
                <div className="lg:sticky lg:top-20 lg:md:top-24 relative">
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="lg:hidden absolute top-2 right-2 z-10 p-2 bg-namke-cream rounded-full hover:bg-primary-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <RestaurantFiltersPanel
                    filters={draftRestaurantFilters}
                    expandedSections={expandedRestaurantSections}
                    onToggleSection={toggleRestaurantSection}
                    onToggleFilter={toggleRestaurantFilter}
                    onClearAllFilters={clearAllRestaurantFilters}
                    onPriceRangeChange={handleRestaurantPriceRangeChange}
                    onApplyFilters={applyRestaurantFilters}
                  />
                </div>
              </div>

              {/* Contenu des restaurants */}
              <div className="flex-1">
                <RestaurantsSection
                  restaurants={restaurants}
                  restaurantsLoading={restaurantsLoading}
                  filteredRestaurants={filteredRestaurants}
                  onRestaurantCardClick={handleRestaurantCardClick}
                  restaurantSortBy={restaurantSortBy}
                  showRestaurantSortDropdown={showRestaurantSortDropdown}
                  onRestaurantSortChange={setRestaurantSortBy}
                  onToggleRestaurantSortDropdown={() => setShowRestaurantSortDropdown(!showRestaurantSortDropdown)}
                  serviceMode={serviceMode}
                  onServiceModeChange={setServiceMode}
                />
              </div>
            </div>
          )}

          {tab === 'groceryShop' && (
            <div>
              {groceryCategories.length > 0 && (
                <div className="mb-6">
                  <div
                    className="flex gap-2 overflow-x-auto snap-x snap-proximity scroll-smooth pb-2 [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    {['Tous', ...groceryCategories].map((category) => {
                      const isSelected = (selectedGroceryCategory || 'Tous') === category;
                      const label = category === 'Tous' ? t('home.allCategories') : category;
                      return (
                        <button
                          key={category}
                          type="button"
                          onClick={() => handleGroceryCategoryClick(category)}
                          className={`flex-shrink-0 snap-start flex items-center gap-2 rounded-2xl p-3 text-sm whitespace-nowrap transition-colors ${
                            isSelected ? 'font-semibold text-namke-terracotta bg-secondary-100' : 'text-namke-black/70 hover:bg-primary-50 bg-namke-cream'
                          }`}
                        >
                          <img src={getGroceryCategoryIcon(category)} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Mobile : liste complète des magasins, chaque ligne mène à sa page détail */}
              {groceryShops.length > 0 && (
                <div className="md:hidden mb-6 space-y-2">
                  <h3 className="text-sm font-semibold text-namke-black/60 mb-1 uppercase tracking-wide">
                    {t('home.shops')}
                  </h3>
                  {groceryShops.map((shop) => (
                    <button
                      key={shop.id}
                      type="button"
                      onClick={() => navigate(`/grocery-shop/${shop.id}`)}
                      className="w-full flex items-center gap-3 bg-namke-white border border-namke-darkGreen/10 rounded-2xl p-3 text-left hover:bg-primary-50 transition-colors"
                    >
                      {shop.image_url ? (
                        <img src={shop.image_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-sm"
                          style={{ backgroundColor: 'rgb(5, 70, 37)' }}
                        >
                          {(shop.name || '?').split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('')}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-sm font-semibold text-namke-black truncate">{shop.name}</p>
                        {shop.city && <p className="text-xs text-namke-black/50 truncate">{shop.city}</p>}
                      </div>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="w-4 h-4 text-namke-black/35 flex-shrink-0">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}

              {/* Desktop : carrousel de magasins qui filtre la grille en place */}
              <div className="hidden md:block">
                <GroceryShopsCarousel
                  shops={groceryShops}
                  selectedShopId={selectedGroceryShopId}
                  onSelectShop={(id) => {
                    setSelectedGroceryShopId(id);
                    setGroceryPage(1);
                  }}
                />
              </div>
              <GroceryShopSection
                products={pagedGroceryProducts}
                loading={groceryShopLoading}
                totalCount={filteredGroceryProducts?.length ?? 0}
                currentPage={groceryPageSafe}
                totalPages={groceryTotalPages}
                onPageChange={handleGroceryPageChange}
              />
            </div>
          )}

          {tab === 'dishes' && (
            <>
              {/* Filtres et contenu principal pour les plats */}
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Bouton pour afficher/masquer les filtres - invisible sur grands écrans */}
                <div className="flex items-center justify-between mb-4 lg:hidden">
                  <button
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-namke-darkGreen text-white rounded-full hover:bg-namke-black transition-colors"
                  >
                    <Filter className="w-5 h-5" />
                    <span>{showMobileFilters ? t('home.hideFilters') : t('home.showFilters')}</span>
                  </button>
                </div>

                <div className={`w-full lg:w-80 flex-shrink-0 transition-all duration-300 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
                  <div className="lg:sticky lg:top-20 lg:md:top-24 relative">
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="lg:hidden absolute top-2 right-2 z-10 p-2 bg-namke-cream rounded-full hover:bg-primary-100 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <FiltersPanel
                      filters={draftDishFilters}
                      expandedSections={expandedSections}
                      onToggleSection={toggleSection}
                      onToggleFilter={toggleFilter}
                      onClearAllFilters={clearAllFilters}
                      onPriceRangeChange={handlePriceRangeChange}
                      onApplyFilters={applyDishFilters}
                    />
                    
                    {/* Promotion flottante juste en bas des filtres - masquée sur mobile */}
                    <div className="hidden lg:block mt-4">
                      <PromotionsFloatingManager />
                    </div>
                  </div>
                </div>

                {/* Promotion flottante sur mobile - toute la largeur */}
                <div className="lg:hidden mb-4 w-full">
                  <PromotionsFloatingManager />
                </div>

                <div className="flex-1">
                  <DishesSection
                    dishes={serviceDishes}
                    dishesLoading={searchQueryFromUrl ? searchResultsLoading : dishesLoading}
                    currentDishes={currentDishes}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onDishCardClick={handleDishCardClick}
                    onDishPlusClick={handleDishPlusClick}
                    sortBy={sortBy}
                    showSortDropdown={showSortDropdown}
                    onSortChange={setSortBy}
                    onToggleSortDropdown={() => setShowSortDropdown(!showSortDropdown)}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <CateringServicesCarousel
          services={cateringServices}
          loading={cateringLoading}
          error={cateringError}
        />

        {/* Section Promotions */}
        <PromotionsSectionUpdated />
      </div>

      {/* Promotions en bande en haut (affichées en position fixe) */}
      <PromotionsBannerManager />

      <Footer />
    </>
  );
};

export default Home;